import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useRequest } from 'ahooks';
import { FormikHelpers } from 'formik';
import { FuelTypography } from '@frete.com/fuel-react';
import * as toolkit from '@frete.com/toolkit-sdk';

import {
  Checkbox,
  CurrencyInput,
  Dropdown,
  Footer,
  Form,
  Grid,
  Info,
  Input,
  Loading as InputLoading,
  MaskedInput,
  Tooltip,
} from '~components';
import states from '~constants/federationStates';
import { cnpjFormInitialValues } from '~constants/initialValues';
import { OnboardingStatusEnum } from '~constants/onboardingStatus';
import { onlyNumbers } from '~constants/regularExpressions';
import { useLayout, useOnboardingLink } from '~hooks';
import { makeOnboardingNcaTrack } from '~services/tracking/onboarding-nca';
import { makeOnboardingTrackingSDK } from '~services/tracking/tracking.config';
import { Address, Document, Federation, Onboarding } from '~services';

import { CnpjFieldIcon, CompanyDetails, Loading, TermsLabel } from './sections';

import {
  formatCitiesOptions,
  formatCnpjInspectResponse,
  formatPayload,
} from './formatters';
import CompanyTypeInformation from './sections/CompanyTypeInformation';
import * as Styled from './styled';
import { Option, Product, Settler, Values } from './types';
import validationSchema from './validation';
import * as amplitude from '@amplitude/analytics-browser';

const RESUMABLE_ONBOARDING_STATUSES: string[] = [
  OnboardingStatusEnum.CREATED,
  OnboardingStatusEnum.CREDIT_EVALUATION,
  OnboardingStatusEnum.REPRESENTATIVES_REGISTER,
];

function Cnpj() {
  const [searchParams] = useSearchParams();
  const cnpjParam = searchParams.get('cnpj');
  const product = searchParams.get('product') as Product;

  const defaultValues = {
    isAllFieldsVisible: false,
    cnpj: cnpjParam || '',
    tradingName: '',
    zipCode: '',
    street: '',
    number: '',
    neighborhood: '',
    state: '',
    city: '',
    complement: '',
    revenue: '',
    terms: false,
  };

  const [zipCodeError, setZipCodeError] = useState('');
  const [addressNumberError, setAddressNumberError] = useState('');
  const [revenueError, setRevenueError] = useState('');
  const [initialValues, setInitialValues] = useState(defaultValues);
  const [isAllAddressFieldsDisabled, setAllAddressFieldsDisabled] =
    useState(true);
  const [citiesOptions, setCitiesOptions] = useState([]);
  const [companyName, setCompanyName] = useState('');
  const [activityCode, setActivityCode] = useState('');
  const [establishmentDate, setEstablishmentDate] = useState('');
  const [legalNature, setLegalNature] = useState(null);
  const [onboardingCurrentStatus, setOnboardingCurrentStatus] = useState('');

  const { setLayout } = useLayout();
  const navigate = useNavigate();
  const { uuid: linkUuid } = useParams();
  const {
    companyData: linkCompany,
    isLocked,
    isLoading: loadingLink,
    error: linkError,
  } = useOnboardingLink();

  const trackNca = useMemo(() => {
    if (!linkUuid) return null;

    const sdk = makeOnboardingTrackingSDK(linkUuid);

    return sdk?.track ? makeOnboardingNcaTrack(sdk) : null;
  }, [linkUuid]);

  useEffect(() => {
    if (linkCompany) trackNca?.cnpjCarregado();
  }, [linkCompany, trackNca]);

  useEffect(() => {
    amplitude.track({
      event_type: 'onboarding_started',
    });
  }, []);

  const { runAsync: fetchCnpjData, loading: loadingCnpjData } = useRequest(
    Document.getDataDocument,
    {
      manual: true,
    }
  );

  const { runAsync: fetchAddressByZipCode, loading: loadingAddressByZipCode } =
    useRequest(Address.getAddressByZipCode, {
      onError: () => setZipCodeError('CEP não encontrado.'),
      manual: true,
    });

  const { run: fetchCitiesByStateId, loading: loadingCities } = useRequest(
    Federation.getCitiesByStateId,
    {
      onSuccess: (response) => {
        const formattedCities = formatCitiesOptions(response?.data);
        setCitiesOptions(formattedCities);
      },
      manual: true,
    }
  );

  const { runAsync: createOnboarding } = useRequest(Onboarding.create, {
    manual: true,
  });

  const { runAsync: fetchSubmittedOnboarding } = useRequest(
    Document.getDataDocument,
    {
      manual: true,
    }
  );

  const updateCompanyDetails = useCallback(
    async (cnpj: string, settler: Settler) => {
      const cnpjWithoutMask = toolkit.cnpj.normalize(cnpj);
      const response = await fetchCnpjData(cnpjWithoutMask);
      const onboardingStatus = response?.data?.status;
      setOnboardingCurrentStatus(onboardingStatus);

      amplitude.track({
        event_type: 'CNPJ_inserted',
      });

      const identifyEvent = new amplitude.Identify().set(
        'CNPJ',
        cnpjWithoutMask
      );
      amplitude.identify(identifyEvent);

      if (
        onboardingStatus &&
        !RESUMABLE_ONBOARDING_STATUSES.includes(onboardingStatus)
      ) {
        navigate(`entrar/cpf`, { state: { cnpj: cnpjWithoutMask } });
        return;
      }

      if (response?.data?.legal_nature) {
        localStorage.setItem(
          'legal_nature',
          String(response?.data?.legal_nature)
        );
      }

      if (response?.data?.activity_code) {
        setActivityCode(response?.data?.activity_code);
      }

      let address = {};

      if (response?.data?.state) {
        setAllAddressFieldsDisabled(false);
        fetchCitiesByStateId(response?.data?.state);
      } else if (response?.data?.zip_code) {
        const zipCodeResponse = await fetchAddressByZipCode(
          response?.data?.zip_code
        );
        address = {
          city: zipCodeResponse.data.city,
          neighborhood: zipCodeResponse.data.neighborhood,
          state: zipCodeResponse.data.state,
          street: zipCodeResponse.data.street,
        };
        setAllAddressFieldsDisabled(false);
        fetchCitiesByStateId(zipCodeResponse.data.state);
      }

      const allValues = { ...response, cnpj };
      const {
        companyName,
        establishmentDate,
        legalNature,
        ...formattedValues
      } = formatCnpjInspectResponse(allValues);
      setCompanyName(companyName);
      setEstablishmentDate(establishmentDate);
      setLegalNature(legalNature);
      settler({ isAllFieldsVisible: true, ...formattedValues, ...address });
    },
    []
  );

  useEffect(() => {
    if (cnpjParam) {
      const cnpjWithoutMask = toolkit.cnpj.normalize(cnpjParam);
      if (cnpjWithoutMask.length === 14) {
        updateCompanyDetails(cnpjWithoutMask, setInitialValues);
      }
    }
  }, [cnpjParam, updateCompanyDetails]);

  useEffect(() => {
    if (!linkCompany) return;

    const { companyName, establishmentDate, legalNature, ...formattedValues } =
      formatCnpjInspectResponse({
        data: linkCompany,
        cnpj: linkCompany.cnpj,
      });

    if (legalNature) {
      localStorage.setItem('legal_nature', String(legalNature));
    }

    setCompanyName(companyName);
    setEstablishmentDate(establishmentDate);
    setLegalNature(legalNature);
    setActivityCode(linkCompany.activity_code ?? '');
    setInitialValues((current) => ({
      ...current,
      ...formattedValues,
      isAllFieldsVisible: true,
    }));
  }, [linkCompany]);

  useEffect(() => {
    if (linkError) {
      navigate('/erro/link', { replace: true });
    }
  }, [linkError, navigate]);

  const fetchSubmittedOnboardingStatus = async (cnpj: string) => {
    const response = await fetchSubmittedOnboarding(cnpj).catch(
      () => undefined
    );

    return response?.data?.status;
  };

  const navigateToNextStep = (
    onboardingId: string,
    status: string | undefined,
    cnpj: string
  ) => {
    if (status === OnboardingStatusEnum.REPRESENTATIVES_REGISTER) {
      navigate(`/representantes/cadastrar?id=${onboardingId}`);
      return;
    }

    if (status && !RESUMABLE_ONBOARDING_STATUSES.includes(status)) {
      navigate('/entrar/cpf', { state: { cnpj } });
      return;
    }

    navigate(`/credito?id=${onboardingId}`);
  };

  const handleSubmitEntireForm = async (
    values: Values,
    helpers: FormikHelpers<Values>
  ) => {
    const payload = formatPayload(
      values,
      companyName,
      establishmentDate,
      legalNature,
      product,
      activityCode,
      linkUuid
    );

    try {
      const response = await createOnboarding(payload);

      helpers.resetForm();

      const status = linkUuid
        ? await fetchSubmittedOnboardingStatus(payload.cnpj)
        : onboardingCurrentStatus;

      navigateToNextStep(response.data.id, status, payload.cnpj);
    } catch (error) {}
  };

  const handleSubmit = async (
    values: Values,
    helpers: FormikHelpers<Values>
  ) => {
    if (values.isAllFieldsVisible) {
      handleSubmitEntireForm(values, helpers);
    } else {
      handleSubmitCnpj(values, helpers);
    }
  };

  const handleSubmitCnpj = async (
    values: Values,
    helpers: FormikHelpers<Values>
  ) => {
    try {
      await updateCompanyDetails(values.cnpj, helpers.setValues);
      helpers.setTouched({});
    } catch (error) {
      helpers.setFieldError('cnpj', 'CNPJ inexistente ou incorreto.');
    }
  };

  const handleChangeNumber = (
    event: ChangeEvent<HTMLInputElement>,
    setFieldValue: FormikHelpers<Values>['setFieldValue']
  ) => {
    const newValue = event.target.value.replace(/D/, '').slice(0, 6);
    setFieldValue('number', newValue);
  };

  const handleChangeZipCode = async (
    event: ChangeEvent<HTMLInputElement>,
    setFieldValue: FormikHelpers<Values>['setFieldValue'],
    state: string
  ) => {
    if (event.target.value.length < 9 && state) {
      setFieldValue('city', '');
      setFieldValue('neighborhood', '');
      setFieldValue('state', '');
      setFieldValue('street', '');
    }

    if (event.target.value.length === 9 && !loadingAddressByZipCode) {
      const zipCodeResponse = await fetchAddressByZipCode(
        event.target.value.replace(onlyNumbers, '')
      );

      setAllAddressFieldsDisabled(false);
      fetchCitiesByStateId(zipCodeResponse.data.state);

      setFieldValue('city', zipCodeResponse.data.city);
      setFieldValue('neighborhood', zipCodeResponse.data.neighborhood);
      setFieldValue('state', zipCodeResponse.data.state);
      setFieldValue('street', zipCodeResponse.data.street);
    } else {
      setZipCodeError('');
      setAllAddressFieldsDisabled(true);
    }
  };

  const handleSelectState = (selected: Option) =>
    fetchCitiesByStateId(selected.id);

  useEffect(() => {
    setLayout({ header: { position: 'left' } });
  }, [setLayout]);

  return (
    <Form<Values>
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      aria-labelledby="form-cnpj-title"
      enableReinitialize
    >
      {({ values, isValid, isInitialValid, errors, ...helpers }) => {
        return (
          <>
            <Styled.Spacing spacing={16} />
            <Grid container direction="column" spacing={3}>
              <Grid>
                <FuelTypography
                  id="form-cnpj-title"
                  color="neutral-strong"
                  size="heading-md"
                  weight="bold"
                >
                  Consultar CNPJ
                </FuelTypography>
              </Grid>
              <Grid>
                <FuelTypography size="body-sm" color="neutral-default">
                  Digite o CNPJ de sua empresa para uma primeira etapa de
                  avaliação.
                </FuelTypography>
              </Grid>
              <Grid>
                <MaskedInput
                  mask="**.***.***/****-99"
                  formatChars={{
                    '*': '[A-Za-z0-9]',
                    '9': '[0-9]',
                  }}
                  name="cnpj"
                  label="CNPJ *"
                  type="tel"
                  width="300px"
                  placeholder="Digite o CNPJ"
                  title="Digite o CNPJ"
                  disabled={isLocked}
                  onChange={(e) => {
                    helpers.setFieldValue('cnpj', e.target.value.toUpperCase());
                  }}
                  trailingIcon={
                    <CnpjFieldIcon
                      disabled={!isValid}
                      locked={isLocked}
                      show={values.isAllFieldsVisible}
                      onClose={() => {
                        helpers.setValues(cnpjFormInitialValues);
                        helpers.setTouched({});
                      }}
                    />
                  }
                />
              </Grid>
            </Grid>
            <Styled.Spacing spacing={16} />
            {!values.isAllFieldsVisible && <CompanyTypeInformation />}
            {values.isAllFieldsVisible && !loadingCnpjData && (
              <>
                <CompanyDetails
                  companyName={companyName}
                  establishmentDate={establishmentDate}
                />
                <Styled.Spacing spacing={10} />
                <FuelTypography size="body-sm" color="neutral-default">
                  Agora precisamos que você confirme ou se necessário corrija os
                  dados abaixo:
                </FuelTypography>
                <Styled.Spacing spacing={10} />
                <Grid container direction="column" spacing={8}>
                  <Grid $fill>
                    <Input
                      name="tradingName"
                      placeholder="Digite o nome fantasia"
                      label="Nome fantasia *"
                      maxLength={100}
                      width="100%"
                    />
                  </Grid>
                  <Styled.FieldRow container spacing={6}>
                    <Grid xs={3}>
                      <MaskedInput
                        name="zipCode"
                        mask="99999-999"
                        placeholder="Digite o CEP"
                        label="CEP *"
                        type="tel"
                        trailingIcon={
                          loadingAddressByZipCode && <InputLoading />
                        }
                        onChange={async (event) => {
                          await handleChangeZipCode(
                            event,
                            helpers.setFieldValue,
                            values.state
                          );

                          helpers.setFieldTouched('neighborhood');
                          helpers.setFieldTouched('street');
                        }}
                        externalError={zipCodeError}
                      />
                    </Grid>
                    <Grid xs={9}>
                      <Input
                        name="street"
                        placeholder="Digite o logradouro"
                        label="Logradouro *"
                        width="100%"
                        maxLength={40}
                        disabled={isAllAddressFieldsDisabled}
                      />
                    </Grid>
                  </Styled.FieldRow>
                  <Styled.FieldRow container spacing={6}>
                    <Grid xs={3}>
                      <Input
                        name="number"
                        type="number"
                        placeholder="Digite o número"
                        externalError={addressNumberError}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                          handleChangeNumber(event, helpers.setFieldValue)
                        }
                        label={
                          <>
                            Número *
                            <Styled.IconWrapper>
                              <Tooltip
                                text="Caso não tenha número, digite 1 (um)."
                                placement="left"
                              >
                                <Info />
                              </Tooltip>
                            </Styled.IconWrapper>
                          </>
                        }
                        disabled={isAllAddressFieldsDisabled}
                      />
                    </Grid>
                    <Grid xs={9}>
                      <Input
                        name="neighborhood"
                        placeholder="Digite o bairro"
                        label="Bairro *"
                        maxLength={40}
                        width="100%"
                        disabled={isAllAddressFieldsDisabled}
                      />
                    </Grid>
                  </Styled.FieldRow>
                  <Styled.FieldRow container spacing={6}>
                    <Grid xs={6}>
                      <Dropdown
                        name="state"
                        label="Estado *"
                        width="100%"
                        placeholder="Selecione o estado"
                        options={states}
                        onSelect={handleSelectState}
                        disabled={isAllAddressFieldsDisabled}
                      />
                    </Grid>
                    <Grid xs={6}>
                      <Dropdown
                        name="city"
                        label="Cidade *"
                        trailingIcon={
                          loadingCities && <InputLoading withBackground />
                        }
                        placeholder="Selecione a cidade"
                        width="100%"
                        options={citiesOptions}
                        disabled={isAllAddressFieldsDisabled}
                      />
                    </Grid>
                  </Styled.FieldRow>
                  <Styled.FieldRow container spacing={6}>
                    <Grid xs={5}>
                      <Input
                        name="complement"
                        label="Complemento"
                        maxLength={30}
                        placeholder="Apartamento, sala, condomínio etc."
                        width="100%"
                        disabled={isAllAddressFieldsDisabled}
                      />
                    </Grid>
                    <Grid xs={7}>
                      <CurrencyInput
                        name="revenue"
                        label="Faturamento anual estimado (R$)*"
                        placeholder="Faturamento anual estimado"
                        width="100%"
                        maxLength={15}
                        externalError={revenueError}
                        allowDecimals={false}
                      />
                    </Grid>
                  </Styled.FieldRow>
                  <Grid $fill>
                    <Checkbox
                      markWhenClickOnLabel={false}
                      name="terms"
                      label={<TermsLabel />}
                    />
                  </Grid>
                </Grid>

                <Footer
                  marginTop="64px"
                  columnSM
                  havePaddingBottom
                  rightButton={
                    <Styled.Button
                      variant="primary"
                      type="submit"
                      disabled={!isValid}
                    >
                      Continuar
                    </Styled.Button>
                  }
                >
                  <FuelTypography size="body-sm" color="neutral-default">
                    * Campos obrigatórios.
                  </FuelTypography>
                </Footer>
              </>
            )}
            <Loading visible={loadingCnpjData || loadingLink} />
          </>
        );
      }}
    </Form>
  );
}

export default Cnpj;
